import { ref, computed } from 'vue';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * Who we are talking to the API as.  ACC-002; design.md §5, §13
 *
 * §13's stack table specifies `oidc-client-ts` against accounts.puida.com, and
 * this is it. The development header shortcut remains behind `VITE_DEV_AUTH=1`
 * and is off by default.
 *
 * Verified against the live discovery document on 2026-08-15: `code` only,
 * PKCE `S256` only, RS256, and no implicit flow. So the SPA is a public client
 * with PKCE and no secret — accounts.md §1.3 lists Lasti App's auth method as
 * `none`.
 *
 * Two things deliberately NOT configured:
 *
 * - The nonce check is left on. accounts.md §2.2: ID tokens omitted `nonce`
 *   until 2026-08-14 and relying parties had to relax the check to work at all.
 *   That is fixed, so any config written against the old behaviour must be
 *   undone rather than carried forward.
 * - `loadUserInfo` stays off. Everything needed is in the ID token, and the
 *   extra round trip to `/userinfo` on every load buys nothing.
 */

const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === '1';
const DEFAULT_ORGANISATION = 'org-protocache';
const DEV_STORAGE_KEY = 'lasti.dev.organisation';

const issuer = import.meta.env.VITE_AUTH_ISSUER ?? '';
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID ?? '';

/**
 * accounts.md §1.4 — "CORS is derived from registered redirect URIs. [...] a
 * tenant subdomain with no registered redirect URI cannot call the token
 * endpoint from the browser at all." And §5: a redirect URI must match
 * exactly — no wildcards, and a trailing slash is a different URI.
 *
 * Probed against the live authorize endpoint on 2026-08-15 (ACC-001 — the list
 * in accounts.md §1.3 was inferred, so it was checked rather than trusted):
 *
 *   registered   http://localhost:9070/auth/callback   dev
 *                http://localhost:9071/auth/callback   preview
 *                https://lasti.fi/auth/callback
 *                https://protocache.lasti.fi/auth/callback
 *   NOT          http://localhost:5173/auth/callback   replaced by the 9070 pair
 *                https://app.lasti.fi/auth/callback
 *
 * An unregistered origin does not fail visibly at the redirect — the authorize
 * endpoint answers HTTP 200 with an "Invalid Redirect URI" HTML page, so a
 * status-code check would call it success.
 */
const redirectUri =
  import.meta.env.VITE_AUTH_REDIRECT_URI ?? `${globalThis.location.origin}/auth/callback`;

export const usingDevAuth = DEV_AUTH;

// --- development shortcut ---------------------------------------------------

const devOrganisation = ref<string>(
  globalThis.localStorage?.getItem(DEV_STORAGE_KEY) ?? DEFAULT_ORGANISATION,
);

export function setOrganisation(next: string): void {
  devOrganisation.value = next;
  globalThis.localStorage?.setItem(DEV_STORAGE_KEY, next);
}

// --- real identity ----------------------------------------------------------

export const userManager = DEV_AUTH
  ? null
  : new UserManager({
      authority: issuer,
      client_id: clientId,
      redirect_uri: redirectUri,
      post_logout_redirect_uri: globalThis.location.origin,
      response_type: 'code',
      scope: 'openid profile email',
      // A token in localStorage outlives the tab and survives an XSS for
      // longer; sessionStorage is the smaller blast radius, and the refresh
      // token restores the session on the next visit anyway.
      userStore: new WebStorageStateStore({ store: globalThis.sessionStorage }),
      automaticSilentRenew: true,
      // Discovery advertises `refresh_token`, so there is no need for a hidden
      // iframe — which third-party-cookie rules are steadily breaking anyway.
      revokeTokensOnSignout: true,
    });

const currentUser = ref<User | null>(null);
const ready = ref(DEV_AUTH);

export const user = computed(() => currentUser.value);
export const isReady = computed(() => ready.value);
export const isSignedIn = computed(() => DEV_AUTH || Boolean(currentUser.value));

export const displayName = computed(() => {
  if (DEV_AUTH) return devOrganisation.value;
  const profile = currentUser.value?.profile;
  return profile?.name ?? profile?.email ?? profile?.sub ?? null;
});

/** The `sub`. Shown on the no-access screen so it can be pasted into grant-access. */
export const subject = computed(() => currentUser.value?.profile?.sub ?? null);

export async function initSession(): Promise<void> {
  if (!userManager) return;
  currentUser.value = await userManager.getUser();
  // An expired token is worse than none: it produces 401s that look like a bug.
  if (currentUser.value?.expired) currentUser.value = null;

  userManager.events.addUserLoaded((next) => {
    currentUser.value = next;
  });
  userManager.events.addUserUnloaded(() => {
    currentUser.value = null;
  });
  userManager.events.addSilentRenewError(() => {
    // Renewal failed — the session is over. Clearing it sends the app to the
    // sign-in screen rather than leaving it making 401s.
    currentUser.value = null;
  });

  ready.value = true;
}

export async function signIn(next?: string): Promise<void> {
  // `state` survives the round trip to the IdP and back, which is how the
  // originally requested path returns — the redirect URI cannot carry it,
  // because it has to match the registered value exactly (accounts.md §5).
  //
  // The nonce is supplied explicitly. `oidc-client-ts` only generates one when
  // the response type includes `id_token`, and this is a pure `code` flow — so
  // without this line no nonce is sent, the ID token comes back without one,
  // and there is nothing for the library to check. It validates a nonce it
  // asked for, which is not the same as always validating.
  //
  // PKCE already blocks code injection, so this is defence in depth. But
  // accounts.md §2.2 records that `nonce` was silently dropped until
  // 2026-08-14 and that relying parties had to relax the check to work at all;
  // sending one is how we would notice if that ever regressed, rather than
  // inheriting the workaround.
  await userManager?.signinRedirect({
    nonce: crypto.randomUUID(),
    ...(next ? { state: next } : {}),
  });
}

export async function completeSignIn(): Promise<User | null> {
  if (!userManager) return null;
  const next = await userManager.signinRedirectCallback();
  currentUser.value = next;
  return next;
}

export async function signOut(): Promise<void> {
  if (!userManager) return;
  await userManager.signoutRedirect();
}

/**
 * The one place in the app that knows how a request is authenticated.
 *
 * ACC-002: "`authHeaders()` returns a bearer token; nothing else in the app
 * changes." That held — this is the only file that moved.
 */
export function authHeaders(): Record<string, string> {
  if (DEV_AUTH) return { 'X-Lasti-Dev-Organisation': devOrganisation.value };
  const token = currentUser.value?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Config problems should be visible at startup, not as a blank redirect. */
export function configurationProblem(): string | null {
  if (DEV_AUTH) return null;
  if (!issuer) return 'VITE_AUTH_ISSUER is not set.';
  if (!clientId) return 'VITE_AUTH_CLIENT_ID is not set.';
  if (!/^https:\/\/.+\/[0-9a-f-]{36}$/i.test(issuer)) {
    return `VITE_AUTH_ISSUER must be organisation-scoped (…/<organisation-id>). Got: ${issuer}`;
  }
  return null;
}

export const authConfig = { issuer, clientId, redirectUri };
